import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Mail, Lock, Loader2, AlertCircle, ArrowRight } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

export default function Login() {
  useSEO('login');
  const { login } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [status, setStatus] = useState({ state: 'idle', msg: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: 'loading', msg: '' });

    try {
      const user = await login(formData.email, formData.password);
      // Redirect based on role
      navigate('/dashboard');
    } catch (err) {
      setStatus({ state: 'error', msg: err.message });
    }
  };

  return (
    <Layout>
      <section className="min-h-[80vh] flex items-center justify-center bg-slate-50 py-16">
        <div className="w-full max-w-md px-4">
          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl font-bold text-navy mb-2">
              Welcome Back
            </h1>
            <p className="text-slate-600">
              Sign in to your BevRelay account
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm"
            data-testid="login-form"
          >
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-navy mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="input-field pl-11"
                    placeholder="you@email.com"
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-navy mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    className="input-field pl-11"
                    placeholder="••••••••"
                    data-testid="login-password"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm text-copper hover:text-copper-dark"
                >
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={status.state === 'loading'}
                className="w-full btn-copper justify-center disabled:opacity-60"
                data-testid="login-submit"
              >
                {status.state === 'loading' ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </button>

              {status.state === 'error' && (
                <div className="rounded-xl px-4 py-3 bg-red-50 text-red-800 border border-red-200 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{status.msg}</span>
                </div>
              )}
            </div>
          </form>

          <p className="text-center mt-6 text-slate-600">
            Don't have an account?{' '}
            <Link to="/register" className="text-copper font-medium hover:text-copper-dark">
              Create one
            </Link>
          </p>
        </div>
      </section>
    </Layout>
  );
}
